import axios from 'axios';
import * as ReactGA from 'react-ga';
import * as Settings from './SettingsStore';
import * as Store from './Store';
import cookie from 'cookie_js';

interface Properties {
  [s: string]: string | number | boolean | undefined;
}

interface FacebookSettings {
  appId: string;
  xfbml: boolean;
  version: string;
}

interface Facebook {
  AppEvents: {
    // Log an application event, for example when someone completes your tutorial.
    logEvent: (eventName: string, valueToSum?: number, parameters?: object) => void;

    // When someone makes a purchase in your app, log the event.
    // Currnecy: http://en.wikipedia.org/wiki/ISO_4217
    logPurchase: (purchaseAmount: number, currency: string, parameters?: object) => void;
  };
  init: (settings: FacebookSettings) => void;
}

declare global {
  namespace NodeJS {
    interface ProcessEnv {
      readonly REACT_APP_GA_TOKEN: string;
      readonly REACT_APP_GA_DEBUG?: string;
    }
  }
}

const isProduction = process.env.NODE_ENV === 'production';
const useGaDebug = !!JSON.parse(process.env.REACT_APP_GA_DEBUG || '0');
const fdTracker: ReactGA.Tracker = {
  trackingId: process.env.REACT_APP_GA_TOKEN,
  gaOptions: {
    name: 'fdTracker',
    // https://developers.google.com/analytics/devguides/collection/analyticsjs/cookies-user-id
    // @ts-ignore
    cookieUpdate: true
  }
};

let DISABLE_GA = false;
let DISABLE_FB = false;

if (useGaDebug) {
  window['ga_debug'] = { trace: true };
}

export function initialize(extraTrackers: ReactGA.Tracker[], disable = false) {
  const trackers = [fdTracker, ...extraTrackers];
  if (disable) {
    setGATracking(false, trackers);
  }
  const trackersWithNames = trackers.map((t, idx) => {
    const name = t.gaOptions?.name || `client${idx + 1}`;
    return {
      ...t,
      gaOptions: {
        ...(t.gaOptions || {}),
        name,
        cookieExpires: 360 * 24 * 60 * 60 // need set expiration for all trackers
      }
    };
  });

  const trackerNames = trackersWithNames.map((t) => t.gaOptions.name);

  ReactGA.initialize(trackersWithNames, {
    debug: useGaDebug,
    alwaysSendToDefaultTracker: false
  });

  if (useGaDebug) {
    // don't send data to google
    ReactGA.set({ sendHitTask: null }, trackerNames);
  }

  trackerNames.forEach((tName) => {
    ReactGA.ga(`${tName}.require`, 'ecommerce');
    ReactGA.ga(`${tName}.set`, 'appName', 'flipdish');
  });
}

function clearGACookies() {
  cookie.remove('_ga');
  cookie.remove('_gid');
}

function disableGaTracker(trackerId: string) {
  window[`ga-disable-${trackerId}`] = true;
}
function enableGaTracker(trackerId: string) {
  window[`ga-disable-${trackerId}`] = false;
}

function disableGaTrackers() {
  ReactGA.ga((tracker) => {
    const trackerId = tracker.get('trackingId');
    disableGaTracker(trackerId);
  });
}
function enableGaTrackers() {
  ReactGA.ga((tracker) => {
    const trackerId = tracker.get('trackingId');
    enableGaTracker(trackerId);
  });
}

export function setFBTracking(status: boolean = false) {
  DISABLE_FB = !status;
}

export function setGATracking(status: boolean = false, trackers?: ReactGA.Tracker[]) {
  const gaTrackers = trackers || window.ga.getAll();

  DISABLE_GA = !status;

  gaTrackers.forEach((tracker) => {
    const trackingId = tracker?.get ? tracker.get('trackingId') : tracker.trackingId;
    (DISABLE_GA ? disableGaTracker : enableGaTracker)(trackingId);
  });

  if (DISABLE_GA) {
    clearGACookies();
  }
}

export const TrackEvent = (eventName: string, metadata: Properties) => {
  const params = {
    EventName: eventName,
    Metadata: metadata
  };

  const loadedGeo = Settings.LocationStore.Load();
  var headers = {};

  if (!isProduction) {
    console.log('[TrackEvent] - disabled outside production', params);
    return;
  }

  if (loadedGeo) {
    headers[
      'X-Coordinates'
    ] = `${loadedGeo.Latitude},${loadedGeo.Longitude},${Store.LocationCertainty.Settings}`;
  }

  axios
    .post('/api/events/', params, {
      headers: headers
    })
    .then(() => {
      console.log('Event Tracked Successfully');
    })
    .catch((e) => {
      console.error('Event tracking fail', e);
    });
};

let lastPageTitle: string;
export const PageView = () => {
  try {
    let title = '';

    const configureRegExp = new RegExp(
      '^#/restaurant/[0-9]*/(collection|delivery)/[0-9]*/configure/[0-9]*/0$'
    );
    const restaurantRegExp = new RegExp('^#/restaurant/[0-9]*/(collection|delivery)/[0-9]*$');
    const smsRegExp = new RegExp('#/code/+[0-9]*');
    const paymentRegExp = new RegExp('^#/payment/[0-9]*/(collection|delivery)/[0-9]*$');
    const cardRegExp = new RegExp('#/card/');
    const checkoutRegExp = new RegExp('#/checkout/');
    const voucherRegExp = new RegExp('#/voucher/');
    const orderRegExp = new RegExp('#/order/');

    if (window.location.hash === '#/where') {
      title = 'Map Screen';
    } else if (window.location.hash === '#/delivery/confirm') {
      title = 'Confirm Address Screen';
    } else if (window.location.hash === '#/collection') {
      title = 'Delivery/Collection Restaurants List';
    } else if (configureRegExp.test(window.location.hash)) {
      title = 'Restaurant Menu Options Screen';
    } else if (restaurantRegExp.test(window.location.hash)) {
      title = 'Restaurant Menu Screen';
    } else if (window.location.hash === '#/phone') {
      title = 'Phone Number Screen';
    } else if (smsRegExp.test(window.location.hash)) {
      title = 'SMS Code Screen';
    } else if (paymentRegExp.test(window.location.hash)) {
      title = 'Payments Screen';
    } else if (cardRegExp.test(window.location.hash)) {
      title = 'Add New Card Screen';
    } else if (checkoutRegExp.test(window.location.hash)) {
      title = 'Checkout Screen';
    } else if (voucherRegExp.test(window.location.hash)) {
      title = 'Add Voucher Screen';
    } else if (orderRegExp.test(window.location.hash)) {
      title = 'Order Placed Screen';
    }

    if (lastPageTitle === title) {
      // stops Google Analytics firing twice!
      return;
    }

    lastPageTitle = title;

    TrackEvent('pageview', { url: window.location.href, title });

    if (window.ga) {
      window.ga(function pageView() {
        const trackers = window.ga.getAll();
        trackers.forEach((tracker) => {
          tracker.set({ page: window.location.hash });
          tracker.send('pageview', window.location.hash);
          tracker.send('screenview', { screenName: title });
        });
      });
    }
  } catch (e) {
    console.error('Error sending GA event', e);
  }
};

export const Track = (category: string, action: string, props: Properties = {}) => {
  try {
    TrackEvent(action, props);

    if (isProduction && !DISABLE_GA && window.ga) {
      window.ga(function track() {
        const trackers = window.ga.getAll();
        trackers.forEach((tracker) => {
          tracker.send('event', category, action, props);
        });
      });
    }

    if (isProduction && !DISABLE_FB) {
      (window['FB'] as Facebook)?.AppEvents?.logEvent(action, undefined, props);
    }
  } catch (e) {
    console.error('Error sending event', e);
  }
};

export const EcommerceSend = () => {
  try {
    if (window.ga) {
      window.ga(function ecSend() {
        const trackers = window.ga.getAll();
        trackers.forEach((tracker) => {
          const name = tracker.get('name');
          window.ga(`${name}.ecommerce:send`);
        });
      });
    }
  } catch (e) {
    console.error('Error sending event', e);
  }
};

export const EcommerceTransaction = (orderId: number, basketTotal: number, currency: string) => {
  try {
    if (window.ga) {
      window.ga(function ecAddTransaction() {
        const trackers = window.ga.getAll();
        trackers.forEach((tracker) => {
          const name = tracker.get('name');
          window.ga(`${name}.ecommerce:addTransaction`, {
            id: orderId,
            affiliation: 'order',
            revenue: basketTotal,
            currency
          });
        });
      });
    }
  } catch (e) {
    console.error('Error sending event', e);
  }
};

export const EcommerceItem = (
  orderId: number,
  name: string | undefined,
  sku: string,
  price: number,
  quantity: number,
  currency: string
) => {
  try {
    if (window.ga) {
      window.ga(function ecAddItem() {
        const trackers = window.ga.getAll();
        trackers.forEach((tracker) => {
          const name = tracker.get('name');
          window.ga(`${name}.ecommerce:addItem`, {
            id: orderId,
            name,
            sku,
            category: 'flipdish',
            price,
            quantity,
            currency
          });
        });
      });
    }
  } catch (e) {
    console.error('Error sending event', e);
  }
};

const facebookSettings = {
  appId: 'your-app-id',
  xfbml: true,
  version: 'v2.8'
};

window['fbAsyncInit'] = () => {
  if (!DISABLE_FB && window['FB']) {
    (window['FB'] as Facebook).init(facebookSettings);
  }
};

export const InitFacebook = (appId: string) => {
  console.log('Initializing facebook connect with app-id ', appId);

  if (DISABLE_FB) {
    return;
  }

  facebookSettings.appId = appId;

  const fjs = document.getElementsByTagName('script')[0];
  const id = 'facebook-jssdk';

  if (!document.getElementById(id)) {
    const js = document.createElement('script');
    js.id = id;
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    fjs.parentNode && fjs.parentNode.insertBefore(js, fjs);
  }
};
